/**
 * Исполнитель очереди pending-операций (CC §4.9).
 *
 * Порядок повтора, терминальные ошибки и правила backoff — из §4.9.
 * Очередь обрабатывает ТОЛЬКО вкладка-лидер (CLIENT_WEB.md §9.2 п. 5).
 */
import { api, idempotency, unwrap } from '@shared/api/client'
import { ApiError, isNetworkError, isTerminal } from '@shared/api/errors'
import type { ErrorCode } from '@shared/api/types'
import { combineAbortSignals } from '@shared/lib/abort'
import {
  conversationsRepo,
  isPendingSeq,
  matchesRepo,
  messagesRepo,
  notificationsRepo,
  pendingOpsConflict,
  queueRepo,
  tryUserDb,
  type EditMessageOp,
  type PendingOp,
  type ReceiptsOp,
  type SendMessageOp,
} from '@shared/db'
import { isLeaderTab, onTabMessage, postTabMessage, TAB_ID, type TabMessage } from '@shared/ws/leader'

/** Пока replay не завершён, очередь не трогаем (CC §4.9 п. 2). */
let replayComplete = false
let rerunRequested = false
let activeDrain: Promise<void> | null = null
let userId: string | null = null
let contextGeneration = 0
let contextAbort = new AbortController()
let receiptBatchNotBefore = 0
let receiptWakeTimer: ReturnType<typeof setTimeout> | null = null

const RECEIPT_BATCH_LIMIT = 50
const RECEIPT_BATCH_INTERVAL_MS = 2000

interface QueueRunContext {
  userId: string
  generation: number
  signal: AbortSignal
  /** Reset §4.8 повторяет pending после свежих списков, до нового cursor/replay. */
  allowBeforeReplay: boolean
}

const QUEUE_CONTEXT_CHANGED = Symbol('queue-context-changed')

function invalidateQueueContext(): void {
  contextGeneration += 1
  contextAbort.abort()
  contextAbort = new AbortController()
}

export function setQueueContext(id: string | null): void {
  if (userId !== id) {
    invalidateQueueContext()
    receiptBatchNotBefore = 0
    if (receiptWakeTimer) clearTimeout(receiptWakeTimer)
    receiptWakeTimer = null
  }
  userId = id
}

export function setReplayComplete(value: boolean): void {
  // reset/full-resync создаёт новую границу snapshot. Запрос из предыдущего
  // состояния может завершиться позже, но не должен менять или удалять
  // операции, пока replay снова не дойдёт до live.
  if (replayComplete && !value) invalidateQueueContext()
  replayComplete = value
}

function isCurrentContext(context: QueueRunContext): boolean {
  return (
    (replayComplete || context.allowBeforeReplay) &&
    !context.signal.aborted &&
    userId === context.userId &&
    contextGeneration === context.generation
  )
}

function assertCurrentContext(context: QueueRunContext): void {
  if (!isCurrentContext(context)) throw QUEUE_CONTEXT_CHANGED
}

function isSafeEditOperation(op: EditMessageOp): boolean {
  return (
    Number.isSafeInteger(op.base_version) &&
    op.base_version > 0 &&
    Number.isSafeInteger(op.message_seq) &&
    op.message_seq > 0 &&
    typeof op.previous_text === 'string'
  )
}

async function refreshEditedMessage(op: EditMessageOp, context: QueueRunContext): Promise<void> {
  const local = await messagesRepo.byId(op.message_id)
  assertCurrentContext(context)
  const seq = Number.isSafeInteger(op.message_seq) ? op.message_seq : local?.seq
  if (!seq || seq < 1) return

  try {
    const result = await api.GET('/conversations/{conversationId}/messages', {
      params: {
        path: { conversationId: op.conversation_id },
        query: {
          after: seq - 1,
          limit: 1,
          ...(local?.comment_of ? { comment_of: local.comment_of } : {}),
        },
      },
      signal: context.signal,
    })
    assertCurrentContext(context)
    const canonical = unwrap(result).items.find((message) => message.id === op.message_id)
    if (!canonical) return
    const conversation = await conversationsRepo.get(op.conversation_id)
    assertCurrentContext(context)
    await messagesRepo.putConfirmed(canonical, context.userId, conversation?.counterpart_receipts)
  } catch (error) {
    if (error === QUEUE_CONTEXT_CHANGED || !isCurrentContext(context)) throw QUEUE_CONTEXT_CHANGED
    // Outbox уже безопасно завершён. Следующий WS/history проход принесёт канон.
  }
}

async function finishEditConflict(op: EditMessageOp, context: QueueRunContext): Promise<void> {
  await queueRepo.failEditConflict(op)
  assertCurrentContext(context)
  await refreshEditedMessage(op, context)
}

async function reconcileConversationProjection(
  conversationId: string,
  context: QueueRunContext,
): Promise<boolean> {
  const before = await conversationsRepo.get(conversationId)
  assertCurrentContext(context)
  try {
    const result = await api.GET('/conversations/{conversationId}', {
      params: { path: { conversationId } },
      signal: context.signal,
    })
    assertCurrentContext(context)
    await conversationsRepo.putAuthoritative(unwrap(result))
    return true
  } catch (error) {
    if (error === QUEUE_CONTEXT_CHANGED || !isCurrentContext(context)) throw QUEUE_CONTEXT_CHANGED
    if (error instanceof ApiError && (error.code === 'not_found' || error.code === 'not_member')) {
      if (before) {
        await conversationsRepo.deleteIfNotNewer(conversationId, before.version, before.last_seq)
      } else {
        await conversationsRepo.delete(conversationId)
      }
      return true
    }
    return false
  }
}

function isOrderedMutation(op: PendingOp): boolean {
  return (
    op.kind === 'edit_message' ||
    op.kind === 'set_reaction' ||
    op.kind === 'remove_reaction' ||
    op.kind === 'patch_conversation' ||
    op.kind === 'pin_message'
  )
}

function receiptItem(op: ReceiptsOp): Record<string, unknown> {
  const item: Record<string, unknown> = { conversation_id: op.conversation_id }
  if (op.delivered_seq !== undefined) item['delivered_seq'] = op.delivered_seq
  if (op.read_seq !== undefined) item['read_seq'] = op.read_seq
  return item
}

function scheduleReceiptWake(): void {
  if (receiptWakeTimer) clearTimeout(receiptWakeTimer)
  const delay = Math.max(0, receiptBatchNotBefore - Date.now())
  receiptWakeTimer = setTimeout(() => {
    receiptWakeTimer = null
    triggerQueue()
  }, delay)
}

async function waitForReceiptBatchWindow(context: QueueRunContext): Promise<void> {
  const delay = Math.max(0, receiptBatchNotBefore - Date.now())
  if (delay === 0) return

  await new Promise<void>((resolve) => {
    let settled = false
    const finish = (): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      context.signal.removeEventListener('abort', onAbort)
      resolve()
    }
    const onAbort = (): void => finish()
    const timer = setTimeout(finish, delay)
    context.signal.addEventListener('abort', onAbort, { once: true })
  })
  assertCurrentContext(context)
}

async function executeReceiptBatch(
  candidates: readonly ReceiptsOp[],
  context: QueueRunContext,
): Promise<ReceiptsOp[]> {
  if (!candidates.length || Date.now() < receiptBatchNotBefore) return []
  const snapshots = await queueRepo.claimReceiptBatch(candidates, TAB_ID, RECEIPT_BATCH_LIMIT)
  assertCurrentContext(context)
  if (!snapshots.length) return []

  const startedAt = Date.now()
  receiptBatchNotBefore = startedAt + RECEIPT_BATCH_INTERVAL_MS
  scheduleReceiptWake()
  try {
    await api.POST('/receipts', {
      body: { items: snapshots.map(receiptItem) } as never,
      signal: context.signal,
    })
    assertCurrentContext(context)
    await queueRepo.completeReceipts(snapshots)
  } catch (error) {
    if (error === QUEUE_CONTEXT_CHANGED || !isCurrentContext(context)) {
      for (const op of snapshots) await releaseInterruptedOperation(op, context)
      throw QUEUE_CONTEXT_CHANGED
    }
    // Один HTTP-исход относится ко всем exact snapshots батча.
    for (const op of snapshots) await handleFailure(op, error, context)
  }
  return snapshots
}

/* ===================================================================== */
/* Выполнение одной операции                                              */
/* ===================================================================== */

/**
 * Исход выполнения операции.
 *
 * `defer` — операция ещё не может быть выполнена, но остаётся в очереди:
 * так ведёт себя сообщение, ждущее готовности вложения. Снимать его с
 * очереди нельзя, иначе после `media.ready` отправлять будет нечего.
 */
type ExecuteResult = 'done' | 'defer' | 'dropped'

function resetSendFailure(
  op: SendMessageOp,
  conversation: Awaited<ReturnType<typeof conversationsRepo.get>>,
): ErrorCode | null {
  if (!conversation) return 'not_member'
  if (conversation.state !== 'active') return 'conversation_not_sendable'

  if (op.comment_of) {
    if (conversation.kind !== 'channel' || conversation.channel?.format !== 'channel') {
      return 'comment_target_invalid'
    }
    if (!conversation.channel.my_role) return 'not_member'
    if (!conversation.channel.comments_enabled) return 'comments_disabled'
    return null
  }

  if (conversation.kind === 'channel') {
    if (!conversation.channel || conversation.channel.state !== 'active' || !conversation.channel.my_role) {
      return 'not_member'
    }
    if (!conversation.channel.can_post) return 'not_channel_admin'
  }

  return null
}

/**
 * CC §4.8 шаг 3: свежие авторитетные списки позволяют определить терминальную
 * ошибку отправки без запроса. Проверяем все pending, включая находящиеся в
 * backoff: удалённый или закрытый диалог ожиданием не восстановится.
 * `failSend` атомарно меняет outbox и видимое сообщение.
 */
async function preflightResetSends(context: QueueRunContext): Promise<void> {
  assertCurrentContext(context)
  const operations = await queueRepo.all()
  assertCurrentContext(context)

  for (const op of operations) {
    if (op.kind !== 'send_message') continue
    const conversation = await conversationsRepo.get(op.conversation_id)
    assertCurrentContext(context)
    const failure = resetSendFailure(op, conversation)
    if (!failure) continue
    await queueRepo.failSend(op.op_id, op.client_message_id, failure)
    assertCurrentContext(context)
  }
}

async function execute(op: PendingOp, context: QueueRunContext): Promise<ExecuteResult> {
  assertCurrentContext(context)
  switch (op.kind) {
    case 'receipts': {
      const item: Record<string, unknown> = { conversation_id: op.conversation_id }
      if (op.delivered_seq !== undefined) item['delivered_seq'] = op.delivered_seq
      if (op.read_seq !== undefined) item['read_seq'] = op.read_seq

      await api.POST('/receipts', { body: { items: [item] } as never, signal: context.signal })
      return 'done'
    }

    case 'send_message': {
      const local = await messagesRepo.byClientMessageId(op.client_message_id)
      assertCurrentContext(context)
      if (!local) {
        // Пользователь удалил черновик — операция больше не нужна.
        await queueRepo.remove(op.op_id)
        return 'dropped'
      }
      // `ready()` мог вернуть op, а до execute подтверждение успело прийти по WS.
      // Каноническую запись повторно не отправляем и не меняем её state.
      if (!isPendingSeq(local.seq)) {
        await queueRepo.remove(op.op_id)
        return 'dropped'
      }

      const forwardOf = op.forward_of ?? local.local_forward_of ?? undefined
      if (local.forwarded && !forwardOf) {
        // У legacy pending-пересылки source мог быть утрачен вместе со старой op.
        // Без него нельзя незаметно превратить пересылку в обычное text/media сообщение.
        await queueRepo.failSend(op.op_id, op.client_message_id, 'forward_source_invalid')
        return 'dropped'
      }

      // Сообщение с вложением ждёт `media.ready` (CC §5.3).
      if (local.local_state === 'pending_media') return 'defer'

      const claimedPending = await messagesRepo.setLocalStateByClientId(op.client_message_id, {
        local_state: 'sending',
      })
      assertCurrentContext(context)
      // Ещё одно окно гонки: WS мог подтвердить pending между lookup и update.
      if (!claimedPending) return 'dropped'

      const body: Record<string, unknown> = {
        client_message_id: op.client_message_id,
        type: local.type,
      }
      if (local.type === 'encrypted') {
        // Секретный чат (изменение v1.13): наружу уходит только конверт — теми же байтами,
        // что зашифрованы при постановке в очередь, — и id зашифрованного вложения.
        const envelope = op.envelope ?? local.local_envelope
        if (!envelope) {
          await queueRepo.failSend(op.op_id, op.client_message_id, 'validation_failed')
          return 'dropped'
        }
        body['envelope'] = envelope
        if (local.local_media?.media_id) body['media_id'] = local.local_media.media_id
        const sent = await api.POST('/conversations/{conversationId}/messages', {
          params: { path: { conversationId: op.conversation_id } },
          body: body as never,
          ...idempotency(op.idempotency_key ?? op.op_id),
          signal: context.signal,
        })
        assertCurrentContext(context)
        const confirmed = unwrap(sent)
        const secretConversation = await conversationsRepo.get(op.conversation_id)
        assertCurrentContext(context)
        await messagesRepo.confirmSent(
          op.client_message_id,
          confirmed,
          context.userId,
          secretConversation?.counterpart_receipts,
        )
        return 'done'
      }
      if (op.draft_version) body['draft_version'] = op.draft_version
      if (op.topic_id) body['topic_id'] = op.topic_id
      if (local.text && local.type !== 'poll') body['text'] = local.text
      if (local.type === 'poll') body['poll'] = op.poll ?? local.local_poll_create
      if (local.entities?.length && !forwardOf) body['entities'] = local.entities
      if (local.local_media?.media_id) body['media_id'] = local.local_media.media_id
      if (op.media_ids?.length) body['media_ids'] = op.media_ids
      if (local.reply_to?.id) body['reply_to_id'] = local.reply_to.id
      if (op.comment_of) body['comment_of'] = op.comment_of
      if (forwardOf) {
        // Контракт: у пересылки нет своего тела — текст, подпись и вложение копирует сервер,
        // а `type` обязан совпадать с исходным сообщением.
        body['forward_of'] = forwardOf
        delete body['text']
        delete body['media_id']
        delete body['media_ids']
      }

      /*
       * Вложение могло быть выбрано уже после постановки в очередь (C-11):
       * тип уточняется по реальному content_type подтверждённого медиа.
       */
      if (local.local_media?.media_id) {
        body['type'] =
          local.local_media.purpose === 'chat_file'
            ? 'file'
            : local.local_media.content_type?.startsWith('video')
              ? 'video'
              : local.local_media.content_type?.startsWith('audio')
                ? 'voice'
                : 'image'
      }

      const result = await api.POST('/conversations/{conversationId}/messages', {
        params: { path: { conversationId: op.conversation_id } },
        body: body as never,
        ...idempotency(op.idempotency_key ?? op.op_id),
        signal: context.signal,
      })
      assertCurrentContext(context)

      const message = unwrap(result)
      const conversation = await conversationsRepo.get(op.conversation_id)
      assertCurrentContext(context)
      await messagesRepo.confirmSent(
        op.client_message_id,
        message,
        context.userId,
        conversation?.counterpart_receipts,
      )
      return 'done'
    }

    case 'edit_message': {
      // Предыдущая правка могла в этом же проходе продвинуть durable base_version.
      // Используем запись после claim, а не snapshot из начала drain.
      const stored = await queueRepo.get(op.op_id)
      assertCurrentContext(context)
      if (!stored) return 'dropped'
      if (stored.kind !== 'edit_message') return 'done'
      const edit = stored

      // Старые записи r02 не знали base_version. Отправить такую операцию
      // last-write-wins опаснее, чем завершить её и перечитать канон.
      if (!isSafeEditOperation(edit)) {
        await queueRepo.remove(edit.op_id)
        await refreshEditedMessage(edit, context)
        return 'dropped'
      }

      const current = await messagesRepo.byId(edit.message_id)
      assertCurrentContext(context)
      if (current && current.version > edit.base_version) {
        // Потерянный успешный ответ уже подтверждён через WS/history.
        if (current.text === edit.text) {
          await queueRepo.completeEditAndRebaseNext(
            edit,
            { text: current.text, version: current.version },
            edit.base_version,
          )
          return 'dropped'
        }
        // Новая version, даже с тем же previous_text, могла прийти от другого
        // устройства. Без явного rebase предыдущей queued-правкой не перезаписываем её.
        await finishEditConflict(edit, context)
        return 'dropped'
      }

      const baseVersion = edit.base_version
      const result = await api.PATCH('/messages/{messageId}', {
        params: { path: { messageId: edit.message_id } },
        body: {
          text: edit.text,
          entities: edit.entities ?? [],
          base_version: baseVersion,
          ...(edit.draft_version ? { draft_version: edit.draft_version } : {}),
        } as never,
        signal: context.signal,
      })
      assertCurrentContext(context)
      const conversation = await conversationsRepo.get(edit.conversation_id)
      assertCurrentContext(context)
      const canonical = unwrap(result)
      await messagesRepo.putConfirmed(canonical, context.userId, conversation?.counterpart_receipts)
      assertCurrentContext(context)
      await queueRepo.completeEditAndRebaseNext(edit, canonical, baseVersion)
      return 'dropped'
    }

    case 'delete_message':
      await api.DELETE('/messages/{messageId}', {
        params: { path: { messageId: op.message_id }, query: { scope: op.scope } as never },
        signal: context.signal,
      })
      assertCurrentContext(context)
      await messagesRepo.finalizeDelete(op.message_id, op.scope)
      return 'done'

    case 'set_reaction':
      await api.PUT('/messages/{messageId}/reaction', {
        params: { path: { messageId: op.message_id } },
        body: { reaction: op.reaction } as never,
        signal: context.signal,
      })
      assertCurrentContext(context)
      if (!(await queueRepo.hasLaterConflict(op))) {
        await messagesRepo.finalizeOwnReaction(op.message_id, context.userId, op.reaction)
      }
      return 'done'

    case 'remove_reaction':
      await api.DELETE('/messages/{messageId}/reaction', {
        params: { path: { messageId: op.message_id } },
        signal: context.signal,
      })
      assertCurrentContext(context)
      if (!(await queueRepo.hasLaterConflict(op))) {
        await messagesRepo.finalizeOwnReaction(op.message_id, context.userId, null)
      }
      return 'done'

    case 'patch_conversation': {
      const body: Record<string, unknown> = {}
      if (op.patch.muted_until !== undefined) body['muted_until'] = op.patch.muted_until
      if (op.patch.archived !== undefined) body['archived'] = op.patch.archived
      if (op.patch.pinned_order !== undefined) body['pinned_order'] = op.patch.pinned_order
      if (op.patch.manual_unread !== undefined) body['manual_unread'] = op.patch.manual_unread

      const result = await api.PATCH('/conversations/{conversationId}', {
        params: { path: { conversationId: op.conversation_id } },
        body: body as never,
        signal: context.signal,
      })
      assertCurrentContext(context)
      await conversationsRepo.put(unwrap(result))
      return 'done'
    }

    case 'pin_message': {
      const path = '/conversations/{conversationId}/pin' as const
      const params = {
        params: { path: { conversationId: op.conversation_id } },
        signal: context.signal,
      }
      const result = op.message_id
        ? await api.PUT(path, { ...params, body: { message_id: op.message_id } })
        : await api.DELETE(path, params)
      assertCurrentContext(context)
      await conversationsRepo.put(unwrap(result))
      return 'done'
    }

    case 'match_seen':
      await api.POST('/matches/{matchId}/seen', {
        params: { path: { matchId: op.match_id } },
        signal: context.signal,
      })
      assertCurrentContext(context)
      await matchesRepo.finalizeSeen(op.match_id)
      return 'done'

    case 'notifications_read':
      // Контракт: либо список `ids`, либо `all_before` — «всё до момента нажатия».
      // Пустой список означает «прочитать все» на момент постановки операции.
      await api.POST('/notifications/read', {
        body: op.notification_ids.length ? { ids: op.notification_ids } : { all_before: op.created_at },
        signal: context.signal,
      })
      assertCurrentContext(context)
      await notificationsRepo.finalizeRead(
        context.userId,
        op.notification_ids,
        op.notification_ids.length ? null : op.created_at,
        op.created_at,
      )
      return 'done'

    default:
      // Неизвестный вид операции держать в очереди бессмысленно.
      return 'done'
  }
}

/* ===================================================================== */
/* Обработка ошибок операции                                              */
/* ===================================================================== */

async function handleFailure(op: PendingOp, error: unknown, context: QueueRunContext): Promise<void> {
  assertCurrentContext(context)
  const code: ErrorCode | null = error instanceof ApiError ? error.code : null
  const retryAfter = error instanceof ApiError ? error.retryAfter : null

  if (
    op.kind === 'edit_message' &&
    error instanceof ApiError &&
    error.code === 'conflict' &&
    error.details.some((detail) => detail.reason === 'edit_version_conflict')
  ) {
    await finishEditConflict(op, context)
    return
  }

  // Любая terminal-ошибка edit отменяет только всё ещё видимую optimistic-копию,
  // затем пытается получить канон. Guard в failEditConflict защищает новую WS-версию.
  if (op.kind === 'edit_message' && isTerminal(error)) {
    await finishEditConflict(op, context)
    return
  }

  // DELETE scope=me после lost response законно повторяется как 404: это доказанный
  // desired final state, а не повод оставить stale сообщение в IndexedDB.
  if (op.kind === 'delete_message' && code === 'not_found') {
    await messagesRepo.finalizeDelete(op.message_id, op.scope)
    assertCurrentContext(context)
    await queueRepo.remove(op.op_id)
    return
  }

  if (op.kind === 'patch_conversation' && isTerminal(error)) {
    const reconciled = await reconcileConversationProjection(op.conversation_id, context)
    assertCurrentContext(context)
    if (reconciled) await queueRepo.remove(op.op_id)
    else await queueRepo.recordFailure(op.op_id, code, retryAfter)
    return
  }

  /*
   * CC §4.9 п. 5: 403 conversation_not_sendable снимает операцию с очереди,
   * сообщение переходит в `failed`, диалог перечитывается.
   * Автоматический повтор ЗАПРЕЩЁН.
   */
  if (code === 'conversation_not_sendable' || code === 'not_member') {
    if (op.kind === 'send_message') {
      await queueRepo.failSend(op.op_id, op.client_message_id, code)
    } else await queueRepo.remove(op.op_id)
    assertCurrentContext(context)

    if ('conversation_id' in op) await reconcileConversationProjection(op.conversation_id, context)
    return
  }

  // Сессия кончилась — операция ОСТАЁТСЯ в очереди (CC §4.9, §4.11).
  if (code === 'session_expired' || code === 'unauthenticated') {
    if (op.kind === 'send_message') {
      await messagesRepo.setLocalStateByClientId(op.client_message_id, { local_state: 'queued' })
      assertCurrentContext(context)
    }
    await queueRepo.release(op.op_id)
    return
  }

  // Медиа ещё обрабатывается: send-op остаётся durable, а видимое
  // сообщение снова ждёт `media.ready`. Считать 409 terminal нельзя:
  // это теряло операцию и заставляло человека создавать сообщение заново.
  if (code === 'media_not_ready' && op.kind === 'send_message') {
    await messagesRepo.setLocalStateByClientId(op.client_message_id, {
      local_state: 'pending_media',
      error_code: null,
    })
    assertCurrentContext(context)
    await queueRepo.release(op.op_id)
    return
  }

  // Терминальные коды: повтор бессмысленен.
  if (isTerminal(error)) {
    if (op.kind === 'send_message') {
      await queueRepo.failSend(op.op_id, op.client_message_id, code)
    } else await queueRepo.remove(op.op_id)
    return
  }

  // Сеть, 503, 500, 429 — повтор с backoff.
  const updated =
    op.kind === 'send_message'
      ? await queueRepo.recordSendFailure(op.op_id, op.client_message_id, code, retryAfter)
      : await queueRepo.recordFailure(op.op_id, code, retryAfter)
  assertCurrentContext(context)
  // Пока запрос был в полёте, подтверждение могло прийти через WS или историю и
  // атомарно снять outbox-операцию. Серверная запись уже канонична: возвращать её
  // в queued после потерянного HTTP-ответа нельзя.
  if (!updated) return

  if (!isNetworkError(error) && code === null) {
    // Непредвиденная ошибка — не зацикливаемся.
    await queueRepo.release(op.op_id)
  }
}

async function releaseInterruptedOperation(op: PendingOp, context: QueueRunContext): Promise<void> {
  // При replay reset база остаётся прежней: снимаем claim, чтобы идемпотентный
  // запрос выполнился сразу после выхода нового replay в live. При смене
  // аккаунта/logout репозитории уже смотрят в другую базу, поэтому не пишем в них.
  if (userId !== context.userId) return
  try {
    await queueRepo.release(op.op_id)
    if (userId !== context.userId) return
    if (op.kind === 'send_message') {
      await messagesRepo.setLocalStateByClientId(op.client_message_id, { local_state: 'queued' })
    }
  } catch {
    // База может закрыться одновременно с logout. Durable-операция остаётся
    // целой, а её claim штатно истечёт в следующей сессии.
  }
}

/* ===================================================================== */
/* Проход по очереди                                                      */
/* ===================================================================== */

/**
 * Один проход очереди. Безопасен при параллельных вызовах: повторный
 * вход отсекается флагом, а между вкладками — захватом операции.
 */
async function runQueuePass(allowBeforeReplay = false, externalSignal?: AbortSignal): Promise<void> {
  if ((!replayComplete && !allowBeforeReplay) || !userId || !isLeaderTab()) return
  const context: QueueRunContext = {
    userId,
    generation: contextGeneration,
    signal: externalSignal ? combineAbortSignals(contextAbort.signal, externalSignal) : contextAbort.signal,
    allowBeforeReplay,
  }
  try {
    if (allowBeforeReplay) await preflightResetSends(context)
    assertCurrentContext(context)
    // Reload/возврат в аккаунт может оставить claim этой вкладки после отмены
    // старого запроса. Параллельного прохода этой вкладки здесь нет благодаря
    // single-flight, поэтому снимаем claim сразу, не ждём общий TTL 60 с.
    await queueRepo.releaseAllBy(TAB_ID)
    assertCurrentContext(context)
    const ready = await queueRepo.ready(Date.now(), { forceRetryable: allowBeforeReplay })
    assertCurrentContext(context)
    const blockedSendConversations = new Set<string>()
    const blockedOrderedOps: PendingOp[] = []

    const receiptCandidates = ready.filter((op): op is ReceiptsOp => op.kind === 'receipts')
    if (allowBeforeReplay) {
      // Reset обязан попытаться отправить весь exact pending snapshot до нового
      // cursor, но общий HTTP-контракт всё равно ограничивает батчи 50/2 с.
      let remaining = receiptCandidates
      while (remaining.length) {
        await waitForReceiptBatchWindow(context)
        const attempted = await executeReceiptBatch(remaining, context)
        assertCurrentContext(context)
        if (!attempted.length) break
        const attemptedIds = new Set(attempted.map((item) => item.op_id))
        remaining = remaining.filter((item) => !attemptedIds.has(item.op_id))
      }
    } else if (receiptCandidates.length && Date.now() >= receiptBatchNotBefore) {
      await executeReceiptBatch(receiptCandidates, context)
      assertCurrentContext(context)
    }

    for (const op of ready) {
      assertCurrentContext(context)
      if (op.kind === 'receipts') continue
      // Сбой или ожидание вложения блокируют только следующие сообщения этого
      // диалога. Независимые диалоги продолжают синхронизацию в том же проходе.
      if (op.kind === 'send_message' && blockedSendConversations.has(op.conversation_id)) continue
      if (isOrderedMutation(op) && blockedOrderedOps.some((blocked) => pendingOpsConflict(blocked, op))) {
        blockedOrderedOps.push(op)
        continue
      }

      // Отправки одного диалога идут строго последовательно (CC §4.9 п. 3).
      const claimed = await queueRepo.claim(op.op_id, TAB_ID)
      assertCurrentContext(context)
      if (!claimed) {
        if (isOrderedMutation(op) && (await queueRepo.get(op.op_id))) blockedOrderedOps.push(op)
        continue
      }

      try {
        const result = await execute(op, context)
        assertCurrentContext(context)

        /*
         * `defer` оставляет операцию в очереди: сообщение ждёт готовности
         * вложения и уйдёт после `media.ready`. Снять её здесь значило бы
         * потерять сообщение молча — оно осталось бы в истории «в очереди»
         * навсегда.
         */
        if (result === 'done') {
          await queueRepo.remove(op.op_id)
        } else if (result === 'defer') {
          await queueRepo.release(op.op_id)
          if (op.kind === 'send_message') blockedSendConversations.add(op.conversation_id)
        }
      } catch (error) {
        if (error === QUEUE_CONTEXT_CHANGED || !isCurrentContext(context)) {
          await releaseInterruptedOperation(op, context)
          throw QUEUE_CONTEXT_CHANGED
        }
        await handleFailure(op, error, context)
        // Следующие сообщения этого диалога не обгоняют неразрешённый исход;
        // операции других диалогов при этом не ждут следующего таймера.
        if (op.kind === 'send_message') blockedSendConversations.add(op.conversation_id)
        if (isOrderedMutation(op) && (await queueRepo.get(op.op_id))) blockedOrderedOps.push(op)
      }
    }
  } catch (error) {
    // Logout/смена аккаунта закрывает старый Dexie, пока запрос или стартовое
    // восстановление ещё могут выполняться. Старый проход не должен продолжиться
    // в репозиториях нового пользователя, а ожидаемая ошибка закрытия не должна
    // выйти как unhandled rejection.
    if (error !== QUEUE_CONTEXT_CHANGED && isCurrentContext(context)) throw error
  }
}

async function drainQueue(allowBeforeReplay = false, externalSignal?: AbortSignal): Promise<void> {
  try {
    do {
      rerunRequested = false
      await runQueuePass(allowBeforeReplay, externalSignal)
    } while (rerunRequested)
  } finally {
    activeDrain = null
  }
}

export function runQueue(): Promise<void> {
  if (activeDrain) {
    // Возвращаемый promise охватывает активный проход и запрошенный повтор,
    // поэтому вызывающий может дождаться фактического простоя.
    if (replayComplete && userId) rerunRequested = true
    return activeDrain
  }
  if (!replayComplete || !userId || !isLeaderTab()) return Promise.resolve()

  const drain = drainQueue()
  activeDrain = drain
  return drain
}

/**
 * Граница только для reset из CC §4.8: свежие списки уже записаны, но cursor
 * snapshot нельзя сохранять до сверки и повтора доступных pending-операций.
 * Обычные фоновые триггеры по-прежнему ждут `replayComplete`; этот явный путь
 * вызывает только SyncEngine.
 */
export async function runQueueAfterResetLists(
  expectedUserId: string,
  externalSignal?: AbortSignal,
): Promise<void> {
  if (activeDrain) await activeDrain
  if (externalSignal?.aborted || replayComplete || userId !== expectedUserId || !isLeaderTab()) return

  const drain = drainQueue(true, externalSignal)
  activeDrain = drain
  await drain
}

/**
 * Безопасный production-trigger для событий UI, сети и таймеров. Прямой
 * `runQueue()` остаётся awaitable для тестов и управляемых orchestration paths,
 * а фоновый вызов всегда завершает rejection здесь и оставляет диагностику.
 */
export function triggerQueue(): void {
  if (!isLeaderTab()) {
    postTabMessage({ type: 'replica_changed' })
    return
  }
  runQueueInBackground()
}

function runQueueInBackground(): void {
  void runQueue().catch((error: unknown) => {
    console.error('background queue run failed', error)
  })
}

/** Сообщение ведомой вкладки запускает очередь только у текущего лидера. */
export function handleQueueTabMessage(message: TabMessage): void {
  if (message.type === 'replica_changed' && isLeaderTab()) runQueueInBackground()
}

/* ===================================================================== */
/* Триггеры повтора (CLIENT_WEB.md §9.2 п. 5)                             */
/* ===================================================================== */

let intervalTimer: ReturnType<typeof setInterval> | null = null
let listenersAttached = false

export function startQueueRunner(): void {
  if (intervalTimer) clearInterval(intervalTimer)
  intervalTimer = setInterval(triggerQueue, 30_000)

  if (listenersAttached) return
  listenersAttached = true

  globalThis.addEventListener('online', triggerQueue)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') triggerQueue()
  })
  onTabMessage(handleQueueTabMessage)
}

export function stopQueueRunner(): void {
  if (intervalTimer) clearInterval(intervalTimer)
  intervalTimer = null
  if (receiptWakeTimer) clearTimeout(receiptWakeTimer)
  receiptWakeTimer = null
}

/** Останавливает leader-runner, дожидается активного прохода и снимает его claim. */
export async function stopQueueRunnerAndRelease(): Promise<void> {
  // Репозитории обычно берут текущую БД. Здесь сохраняем точный
  // экземпляр до await: пока старый drain завершается, уже могла
  // открыться БД другого аккаунта, чьи claims трогать нельзя.
  const database = tryUserDb()
  stopQueueRunner()
  setReplayComplete(false)
  setQueueContext(null)
  try {
    await activeDrain
  } catch {
    // Контекст намеренно отменён потерей лидерства.
  }
  if (database?.isOpen()) {
    try {
      await queueRepo.releaseAllBy(TAB_ID, database)
    } catch {
      // Account switch мог закрыть захваченную БД между isOpen
      // и transaction. Новый эксклюзивный лидер A снимет все stale claims.
    }
  }
}
